/**
 *
 *  SignUpForm
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAppState } from "../../../state";
import { useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";
import PhoneCodeInput from "../../PhoneCodeInput/PhoneCodeInput";
import DialingCodeSelectorPopup from "../../PhoneCodeInput/DialingCodeSelectorPopup/DialingCodeSelectorPopup";
import AppleLogin from "../../AppleLogin/AppleLogin";
import GoogleLogin from "../../GoogleLogin/GoogleLogin";
import FacebookLogin from "../../FacebookLogin/FacebookLogin";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";
import MailIcon from "../../../icons/MailIcon";

// Hooks
import useCountryName from "../../../hooks/useCountryName/useCountryName";

// Utils
import {
  formatPhoneNumber,
  getCountryInfo,
  getCountryInfoByPhoneCode,
  isAndroidDevice,
  isIosDevice,
  isMac,
  parseUrlParts,
  getLocationReturnUrl,
} from "../../../utils";

const isLoginPath = (pathname) => {
  return pathname.includes("/login") || pathname.includes("/become-a-member");
};

export default function SignUpForm({
  className,
  title,
  ctaText,
  dividerText,
  returnTo,
  onNext,
  onSignIn = () => {},
  onCloseClick,
}) {
  const history = useHistory();
  const location = useLocation();

  const {
    checkPhoneNumber,
    verifyPhoneNumber,
    verifyPhoneNumberOrEmailForSimpleLogin,
    checkAppleLogin,
    loginWithApple,
    checkGoogleLogin,
    checkFacebookLogin,
    loginWithGoogle,
    loginWithFacebook,
    clearRegistrationCode,
    error,
    setError,
    isFetching,
  } = useAppState();
  const {
    setAuthState,
    setUserPhoneCode,
    userPhoneCode,
    setUserPhoneNumber,
    userPhoneNumber,
    setUserFirstName,
    setUserFamilyName,
    setUserEmail,
    setIdentityToken,
    setAuthorizationCode,
    setAuthVendor,
    setAccessToken,
  } = useSignUpState();

  const [canSend, setCanSend] = React.useState(false);
  const [phoneCode, setPhoneCode] = React.useState("+1");
  const [countryFlag, setCountryFlag] = React.useState("🇺🇸");
  const [showChooseCodePopup, setShowChooseCodePopup] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [latlng, setLatlng] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [hasError, setHasError] = useState(false);
  const [notificationsConsent, setNotificationsConsent] = useState(true);
  const [termsConsent, setTermsConsent] = useState(true);

  const [, countryShortName, lookupAddress] = useCountryName(latlng);

  const phoneNumberRef = React.useRef();
  const phoneCodeInputRef = React.useRef();
  const isMounted = React.useRef(true); // Create a ref to track if the component is mounted

  useEffect(() => {
    let latLng;

    if (navigator.geolocation /* && !userPhoneCode*/) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (isMounted.current) {
            // Check if the component is still mounted
            latLng = `${pos.coords.latitude},${pos.coords.longitude}`;
            setLatlng(latLng);

            lookupAddress(latLng);
          }
        },
        (err) => {
          console.warn(`ERROR(${err.code}): ${err.message}`);
          if (isMounted.current) {
            // Check if the component is still mounted
            setLatlng(`${34.098907},${-118.327759}`);
          }

          // lookupAddress(latlng);
        }
      );
    }

    return () => {
      isMounted.current = false; // Set the flag to false when unmounted
    };
  }, [userPhoneCode, lookupAddress]);

  useEffect(() => {
    if (countryShortName) {
      let [phoneCode, countryFlag, countryFlagImage] =
        getCountryInfo(countryShortName);

      setPhoneCode(phoneCode);
      setUserPhoneCode(phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);
    }
  }, [setUserPhoneCode, countryShortName]);

  useEffect(() => {
    if (userPhoneCode && userPhoneNumber) {
      let [countryShortName, countryFlag, countryFlagImage] =
        getCountryInfoByPhoneCode(userPhoneCode);

      setPhoneCode(userPhoneCode);
      setPhoneNumber(userPhoneNumber);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);

      if (validatePhoneNumber(userPhoneNumber)) {
        setCanSend(true);
      }
    }
  }, [userPhoneCode, userPhoneNumber]);

  const handlePhoneCodeSelect = (countryInfo) => {
    if (countryInfo) {
      setPhoneCode(countryInfo.phoneCode);
      setUserPhoneCode(countryInfo.phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice
          ? countryInfo.emoji
          : countryInfo.image
      );
      setSelectedCountry(countryInfo.code);
      setShowChooseCodePopup(false);
    }
  };

  const handleDialingCodeSelectorPopupClose = () => {
    setShowChooseCodePopup(false);
  };

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value;

    value = formatPhoneNumber(value);

    setPhoneNumber(value);

    setHasError(false);

    if (validatePhoneNumber(value)) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }
  };

  /**
   * Handles the returnTo URL, normalizing the pathname and query parameters, and replacing the current URL.
   *
   * @param {string} returnTo - The returnTo URL.
   */
  const handleReturnToUrl = useCallback(
    (returnToUrl, isAppleLogin = false) => {
      let { pathname, search } = parseUrlParts(returnToUrl);
      const query = new URLSearchParams(search);

      let stateFrom = window.localStorage.getItem("apple-redirect-state-from");

      if (stateFrom) {
        window.localStorage.removeItem("apple-redirect-state-from");
      }

      if (isLoginPath(pathname)) {
        pathname = "/partners";
      }

      if (isAppleLogin && pathname.includes("/partners")) {
        query.append("apple_login", "yes");
      }

      history.replace({
        pathname,
        state: stateFrom ? { from: stateFrom } : location.state,
        search: query.toString(),
      });
    },
    [location]
  );

  /**
   * Handles the default redirect after login
   * @param {boolean} [isAppleLogin=false] - Whether this is an Apple login
   */
  function handleDefaultRedirect(isAppleLogin = false) {
    let stateFrom = window.localStorage.getItem("apple-redirect-state-from");

    if (stateFrom) {
      window.localStorage.removeItem("apple-redirect-state-from");
    } else {
      /**
       * Get the state from the current location
       * @type {string|undefined}
       */
      stateFrom = location && location.state && location.state.from;
    }

    // FIXME make more robust, quick fix for now
    if (stateFrom && isLoginPath(stateFrom)) {
      stateFrom = undefined;
    }

    /**
     * Replace the current history entry with the default redirect URL
     */
    history.replace(
      stateFrom || {
        pathname: "/partners",
        search: isAppleLogin ? "?apple_login=yes" : "",
        //window.location.search,
      }
    );
  }

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      setAuthVendor("");
      setError(null);

      if (phoneCode && phoneNumber) {
        setUserPhoneNumber(phoneNumber);
        setHasError(false);

        checkPhoneNumber(phoneCode, phoneNumber)
          .then((res) => {
            if (res.status === "EXISTING_CUSTOMER") {
              const urlParams = new URLSearchParams(location.search);
              urlParams.set("authState", "codeVerificationLogin");
              // urlParams.set("authState", "login");

              // Just in case, otherwise when we get to the login page, before useEffect will work,
              // authState would be default 'signUp'. Not sure why the hook doesn't get re-rendered.
              //setAuthState("login");

              // history.push({
              //   pathname: "/login",
              //   state: location.state,
              //   search: urlParams.toString(),
              // });

              return verifyPhoneNumberOrEmailForSimpleLogin({
                phoneCode,
                phoneNumber /*, ""*/,
              }).then((res) => ({ isLogin: true }));

              return Promise.reject();
            } else {
              return verifyPhoneNumber({
                phoneCode,
                phoneNumber,
                authVendor: "",
              });
            }
          })
          .then((res) => {
            onNext("", {}, res.isLogin);
          })
          .catch(() => {});
      }
    },
    [location, phoneCode, phoneNumber]
  );

  const handleLoginWithEmail = (e) => {
    e.preventDefault();

    // If there were previously unfinished registration, clean it
    clearRegistrationCode();

    setAuthVendor("email");
    setError(null);

    onNext("email", {}, undefined, true);
  };

  const handleLoginWithApple = useCallback(
    ({ identityToken, authorizationCode }) => {
      setIdentityToken(identityToken);
      setAuthorizationCode(authorizationCode);
      setHasError(false);
      setError(null);

      checkAppleLogin(identityToken, authorizationCode)
        .then((res) => {
          if (res.existed) {
            loginWithApple(identityToken, authorizationCode)
              .then((res) => {
                onSignIn(res, returnTo, true);
                // let returnTo = window.localStorage.getItem(
                //   "apple-redirect-return-to"
                // );

                // if (returnTo) {
                //   window.localStorage.removeItem("apple-redirect-return-to");

                //   handleReturnToUrl(returnTo, true);
                // } else {
                //   handleDefaultRedirect(true);
                // }
              })
              .catch((err) => {
                if (err) {
                  setHasError(true);
                }
              });
          } else {
            // Store user info for later stages
            setUserFirstName(res.firstName);
            setUserFamilyName(res.lastName);
            setUserEmail(res.email);

            setAuthState("setPhoneNumber");

            return Promise.reject();
          }
        })
        .catch((resp) => {});
    },
    [handleReturnToUrl]
  );

  const handleLoginWithGoogle = useCallback(
    (credential) => {
      setIdentityToken(credential);
      // setAuthorizationCode(authorizationCode);
      setHasError(false);
      setError(null);

      checkGoogleLogin(credential)
        .then((res) => {
          if (res.existed) {
            loginWithGoogle(credential)
              .then((res) => {
                onSignIn(res, returnTo);

                // const returnToUrl =
                //   typeof returnTo === "string"
                //     ? returnTo
                //     : returnTo && // if we went from PrivateRoute's state.from
                //       returnTo.pathname;

                // // Do we need the `!returnTo.includes("/login")` check??
                // if (returnToUrl && !isLoginPath(returnToUrl)) {
                //   handleReturnToUrl(returnToUrl);
                // } else {
                //   handleDefaultRedirect();
                // }
              })
              .catch((err) => {
                if (err) {
                  setHasError(true);
                }
              });
          } else {
            // Store user info for later stages
            // We get full name in firstName from BE
            setUserFirstName(res.firstName);
            setUserFamilyName(res.lastName);
            setUserEmail(res.email);

            onNext("google");

            return Promise.reject();
          }
        })
        .catch((resp) => {});
    },
    [returnTo, handleReturnToUrl]
  );

  /**
   * Callback on get login status request sent to Facebook /me
   *
   * @param accessToken
   * @param email
   * @param name
   */
  const handleLoginWithFacebook = ({ accessToken, email, name }) => {
    setAccessToken(accessToken);
    setAuthVendor("facebook");
    setHasError(false);
    setError(null);

    checkFacebookLogin(accessToken)
      .then((res) => {
        if (res.existed) {
          loginWithFacebook(accessToken)
            .then((res) => {
              onSignIn(res, returnTo);
              // const returnToUrl =
              //   typeof returnTo === "string"
              //     ? returnTo
              //     : returnTo && // if we went from PrivateRoute's state.from
              //       returnTo.pathname;

              // // Do we need the `!returnTo.includes("/login")` check??
              // // FIXME quick fix to prevent to return to the login page again
              // if (returnToUrl && !isLoginPath(returnToUrl)) {
              //   handleReturnToUrl(returnToUrl);
              // } else {
              //   handleDefaultRedirect();
              // }
            })
            .catch((err) => {
              if (err) {
                setHasError(true);
              }
            });
        } else {
          // Store user info for later stages
          // We get full name in firstName from Facebook's /me request

          var fullName = name.split(" "),
            firstName = fullName[0],
            familyName = fullName[fullName.length - 1];

          setUserFirstName(firstName);
          setUserFamilyName(familyName);
          setUserEmail(email);

          onNext("facebook");

          return Promise.reject();
        }
      })
      .catch((resp) => {});
  };

  const handleNotificationsConsentChange = useCallback(() => {
    setNotificationsConsent(!notificationsConsent);
  }, [notificationsConsent]);

  const handleTermsConsentChange = useCallback(() => {
    setTermsConsent(!termsConsent);
  }, [termsConsent]);

  return (
    <>
      <div className={`${className ? `${className} ` : ""}tbk-relative`}>
        <div className="tbk-text-title-bold tbk-text-grey-main">{title}</div>
        <p className="tbk-text-main tbk-mb-3 tbk-text-grey-main">
          Enter your phone number to get started.
        </p>
        <form className="tbk-mb-4" onSubmit={handleSubmit}>
          <div className="tbk-mb-2 tbk-flex">
            <PhoneCodeInput
              ref={phoneCodeInputRef}
              className="tbk-mr-1"
              countryFlag={countryFlag}
              countryShortName={countryShortName}
              phoneCode={phoneCode}
              onClick={() =>
                showChooseCodePopup ? null : setShowChooseCodePopup(true)
              }
            />
            <input
              ref={phoneNumberRef}
              type="tel"
              placeholder="Phone number"
              className="tbk-input-field tbk-text-title-medium tbk-h-7 tbk-grow tbk-pr-2"
              style={{ marginBottom: 0 }}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onKeyUp={handlePhoneNumberChange}
            />
          </div>
          <Button
            type="submit"
            className="tb-button tbk-mb-2 tbk-w-full"
            disabled={
              !canSend || !notificationsConsent || !termsConsent || isFetching
            }
            showLoader={isFetching}
            endIcon={ctaText === "Continue" ? <ArrowRightIcon /> : null}
          >
            {ctaText}
          </Button>
        </form>
        <DialingCodeSelectorPopup
          phoneCodeInputRef={phoneCodeInputRef}
          show={showChooseCodePopup}
          selectedCountry={selectedCountry}
          onSelect={handlePhoneCodeSelect}
          onClose={handleDialingCodeSelectorPopupClose}
        />
      </div>
      <div className="login-or-separator tbk-relative tbk-mt-2 tbk-mb-2 tbk-text-center lg:tbk-mb-0">
        <span className="login-or-separator-text tbk-inline-block tbk-bg-basic-white tbk-py-0 tbk-px-2">
          {dividerText}
        </span>
        <hr className="tbk-mx-auto tbk-mb-4 tbk--mt-1.5 tbk-border-grey-inactive" />

        {process.env.REACT_APP_REGION == "GB" || true ? (
          <Button
            variant="secondary"
            className="tbk-mb-1 tbk-w-full !tbk-py-1.5"
            disabled={isFetching || !notificationsConsent || !termsConsent}
            // startIcon={}
            onClick={handleLoginWithEmail}
          >
            <div className="tbk-flex tbk-items-center">
              <MailIcon className="tbk-mr-1 tbk-text-basic-white" /> Email
            </div>
          </Button>
        ) : null}

        <AppleLogin
          clientId={process.env.REACT_APP_APPLE_SIGNIN_CLIENT_ID}
          redirectURI={process.env.REACT_APP_APPLE_SIGNIN_AUTH_REDIRECT_URL}
          returnTo={returnTo || getLocationReturnUrl(location)}
          stateFrom={location && location.state && location.state.from}
          responseType="code id_token"
          responseMode="form_post"
          scope="name email"
          callback={handleLoginWithApple}
          designProp={{
            height: 52,
            width: 375,
            border_radius: 16,
            scale: 2,
          }}
          disabled={isFetching || !notificationsConsent || !termsConsent}
          className="tbk-mb-1 tbk-w-full"
        />

        {/*<FacebookLogin
          socialId={process.env.REACT_APP_FACEBOOK_APP_ID}
          language="en_US"
          scope="public_profile,email"
          responseHandler={handleLoginWithFacebook}
          xfbml={true}
          fields="name,email,picture"
          version="v2.5"
          disabled={isFetching || !notificationsConsent || !termsConsent}
          className="tbk-mb-1 tbk-w-full"
          buttonText="Facebook"
        />*/}

        <GoogleLogin
          disabled={isFetching || !notificationsConsent || !termsConsent}
          onSuccess={(credentialResponse) => {
            setHasError(false);
            setAuthVendor("google");

            handleLoginWithGoogle(credentialResponse.credential);
            console.log(credentialResponse);
          }}
          onError={() => {
            setHasError(true);

            console.log("Google login Failed");
          }}
          logo_alignment={"center"}
          containerProps={{
            width: "100%",
            height: "54px",
          }}
          width="100%"
          height={54}
        />

        <div className="tbk-mt-4 tbk-mb-1">
          <input
            id={"notificationsConsent"}
            className="tbk-checkbox-small tbk-checkbox-small-grey"
            type="checkbox"
            name="notificationsConsent"
            checked={notificationsConsent}
            onChange={handleNotificationsConsentChange}
          />
          <label
            htmlFor={"notificationsConsent"}
            className="tbk-flex tbk-h-3 tbk-items-center tbk-pl-4"
          >
            <div className="tbk-text-small tbk-text-left tbk-text-blue-grey">
              I agree to receive TruBe notifications via sms and email.
            </div>
          </label>
        </div>
        <div className="tbk-mb-3_">
          <input
            id={"termsConsent"}
            className="tbk-checkbox-small tbk-checkbox-small-grey"
            type="checkbox"
            name="termsConsent"
            checked={termsConsent}
            onChange={handleTermsConsentChange}
          />
          <label
            htmlFor={"termsConsent"}
            className="tbk-flex tbk-h-3 tbk-items-center tbk-pl-4"
          >
            <div className="tbk-text-small tbk-text-left tbk-text-blue-grey">
              I agree to TruBe{" "}
              <a
                className="tbk-text-blue-grey tbk-underline"
                href="/terms/Terms of Use and Booking.pdf"
                target="_blank"
              >
                Terms & Conditions.
              </a>
            </div>
          </label>
        </div>

        {hasError ? (
          <div className="tbk-text-main tbk-mt-1 tbk-mb-2 tbk-text-center tbk-text-coral">
            {error ? error.message : "Unknown error has occurred"}
          </div>
        ) : null}
      </div>

      <p className="tbk-text-small tbk-hidden tbk-text-center tbk-text-grey">
        Registering to this website, you accept our{" "}
        <a
          className="tbk-text-grey tbk-underline hover:tbk-text-grey-inactive"
          href="/terms/Terms of Use and Booking.pdf"
          target="_blank"
        >
          Terms of Use
        </a>{" "}
        and{" "}
        <a
          className="tbk-text-grey tbk-underline hover:tbk-text-grey-inactive"
          href="/terms/TruBe - US Privacy Policy.pdf"
          target="_blank"
        >
          Privacy Policy
        </a>
      </p>
    </>
  );
}

function validatePhoneNumber(phone) {
  return phone && phone.replace(/\s/g, "").length >= 9;
}
